import { Capacitor } from '@capacitor/core';

// Check if running as native app
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', 'web'

// Lazy plugin loaders - prevents crashes if plugin not available
const getPlugin = async (name) => {
  try {
    switch (name) {
      case 'Camera': return await import('@capacitor/camera');
      case 'StatusBar': return await import('@capacitor/status-bar');
      case 'SplashScreen': return await import('@capacitor/splash-screen');
      case 'Share': return await import('@capacitor/share');
      case 'Haptics': return await import('@capacitor/haptics');
      case 'App': return await import('@capacitor/app');
      case 'PushNotifications': return await import('@capacitor/push-notifications');
      default: return null;
    }
  } catch (e) {
    console.warn(`Plugin ${name} not available:`, e.message);
    return null;
  }
};

// Camera - Enhanced food scanning for mobile
export const takePhoto = async () => {
  if (!isNative) return null;
  try {
    const mod = await getPlugin('Camera');
    if (!mod) return null;
    const photo = await mod.Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: mod.CameraResultType.Base64,
      source: mod.CameraSource.Camera,
      width: 1024,
      height: 1024,
    });
    return photo.base64String;
  } catch (e) {
    console.warn('Camera error:', e.message);
    return null;
  }
};

export const pickFromGallery = async () => {
  if (!isNative) return null;
  try {
    const mod = await getPlugin('Camera');
    if (!mod) return null;
    const photo = await mod.Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: mod.CameraResultType.Base64,
      source: mod.CameraSource.Photos,
      width: 1024,
      height: 1024,
    });
    return photo.base64String;
  } catch (e) {
    console.warn('Gallery error:', e.message);
    return null;
  }
};

// Push Notifications
export const initPushNotifications = async (onTokenReceived, onNotification) => {
  if (!isNative) return;
  try {
    const mod = await getPlugin('PushNotifications');
    if (!mod) return;
    const permResult = await mod.PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') return;
    await mod.PushNotifications.register();
    mod.PushNotifications.addListener('registration', (token) => {
      if (onTokenReceived) onTokenReceived(token.value);
    });
    mod.PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (onNotification) onNotification(notification);
    });
    mod.PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      if (onNotification) onNotification(action.notification);
    });
  } catch (e) {
    console.warn('Push notifications not available:', e.message);
  }
};

// Status Bar
export const setStatusBarDark = async () => {
  if (!isNative) return;
  try {
    const mod = await getPlugin('StatusBar');
    if (!mod) return;
    await mod.StatusBar.setStyle({ style: mod.Style.Dark });
    await mod.StatusBar.setBackgroundColor({ color: '#000000' });
  } catch (e) {
    console.warn('StatusBar error:', e.message);
  }
};

// Splash Screen
export const hideSplash = async () => {
  if (!isNative) return;
  try {
    const mod = await getPlugin('SplashScreen');
    if (!mod) return;
    await mod.SplashScreen.hide();
  } catch (e) {
    console.warn('SplashScreen error:', e.message);
  }
};

// Share
export const shareContent = async (title, text, url) => {
  if (!isNative) {
    if (navigator.share) {
      return navigator.share({ title, text, url });
    }
    return;
  }
  try {
    const mod = await getPlugin('Share');
    if (!mod) return;
    await mod.Share.share({ title, text, url, dialogTitle: 'Share via NutriFitness AI' });
  } catch (e) {
    console.warn('Share error:', e.message);
  }
};

// Haptics
export const hapticFeedback = async (style = 'medium') => {
  if (!isNative) return;
  try {
    const mod = await getPlugin('Haptics');
    if (!mod) return;
    const styles = {
      light: mod.ImpactStyle.Light,
      medium: mod.ImpactStyle.Medium,
      heavy: mod.ImpactStyle.Heavy,
    };
    await mod.Haptics.impact({ style: styles[style] || mod.ImpactStyle.Medium });
  } catch (e) {
    console.warn('Haptics error:', e.message);
  }
};

// App lifecycle
export const onAppStateChange = (callback) => {
  if (!isNative) return;
  getPlugin('App').then(mod => {
    if (!mod) return;
    mod.App.addListener('appStateChange', ({ isActive }) => {
      callback(isActive);
    });
  }).catch(() => {});
};

// Back button handler (Android)
export const onBackButton = (callback) => {
  if (!isNative || platform !== 'android') return;
  getPlugin('App').then(mod => {
    if (!mod) return;
    mod.App.addListener('backButton', ({ canGoBack }) => {
      callback(canGoBack);
    });
  }).catch(() => {});
};
